<script setup lang="ts">
    import VueDatePicker from '@vuepic/vue-datepicker';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    import '@vuepic/vue-datepicker/dist/main.css';

    interface Props extends FormInputProps {
        position?: string;
        minTime?: string;
        maxTime?: string;
        disabled: boolean;
    }

    const emit = defineEmits<{
        (e: 'change', value: any): void;
    }>();

    const props = withDefaults(defineProps<Props>(), {
        position: 'top-full left-0',
    });
    const inputData: FormInputData = useFormInput(props);

    const minTime = computed(() => {
        if (!props.minTime) {
            return null;
        }

        return moment(props.minTime).toTimepickerObject();
    });

    const maxTime = computed(() => {
        if (!props.maxTime) {
            return null;
        }

        return moment(props.maxTime).toTimepickerObject();
    });

    const time = computed({
        get() {
            return moment(inputData.model.value).toTimepickerObject();
        },
        set({ hours, minutes }: { hours: number; minutes: number }) {
            const time = moment(inputData.model.value).hour(hours).minute(minutes).toDateTimeString();
            inputData.form.set(inputData.inputName, time);
            if (inputData.inputName === 'maxEndsAt') emit('change', time);
        },
    });

    const formattedTime = computed(() => (inputData.model.value ? moment(inputData.model.value).toShortTimeString() : ''));
</script>

<template>
    <Submenu
        :container="$theme('submenu.container.withoutPosition')"
        without-max-height
        :position="position"
    >
        <template #default="{ show, hide }">
            <FormElement
                :input-data="inputData"
                @label-clicked="show"
            >
                <input
                    :id="inputData.inputId.value"
                    :required="inputData.required.value"
                    :placeholder="inputData.placeholder.value"
                    :class="inputData.className.value"
                    :name="inputData.props.inputName"
                    :value="formattedTime"
                    :disabled="disabled"
                    @focus="show"
                />
            </FormElement>
        </template>
        <template #submenu="{ hide }">
            <div class="py-2">
                <slot
                    name="datepicker"
                    :hide="hide"
                >
                    <VueDatePicker
                        v-model="time"
                        time-picker
                        inline
                        hide-input-icon
                        auto-apply
                        :clearable="false"
                        required
                        :min-time="minTime"
                    />
                </slot>
            </div>
        </template>
    </Submenu>
</template>
